import React, { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import 'moment/locale/en-gb';
import DatePickerCell from './DatePickerCell';
import DatePickerHeader from './DatePickerHeader';
import { DisplayStateEnum } from './DatePickerEnum';
import { createPortal } from 'react-dom';

const DatePickerBody = ({
    currentDate,
    onDateUpdate,
    boundingRect = {},
    expanded = true,
    onClickOutside,
    ...props
}) => {
    const [displayState, setDisplayState] = useState(DisplayStateEnum.DAY);
    const selectedDate = moment(currentDate).isValid() ? moment(currentDate) : moment(new Date());
    const [workingDate, setWorkingDate] = useState(
        moment(currentDate).isValid() ? moment(currentDate) : moment(new Date())
    );
    const [widthOffset, setWidthOffset] = useState(0);

    const bodyBackgroundStyle = {
        height: 'auto',
        width: 'auto',
        border: '2px solid #FD9A00',
        borderRadius: '0 0 5px 5px',
        backgroundColor: '#F0F0F0'
    };

    const bodyContainerStyle = {
        position: 'absolute',
        zIndex: 100,
        paddingTop: 11,
        width: '17.05vw'
    };

    const ref = useRef(null);

    useEffect(() => {
        const width = ref?.current?.getBoundingClientRect()?.width ?? 0;
        setWidthOffset(
            width > 0
                ? ((ref?.current?.getBoundingClientRect()?.width ?? 0) - boundingRect.width) / 2
                : 0
        );
    }, [expanded]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (ref.current && !ref.current.contains(event.target)) {
                onClickOutside(event);
            }
        };
        if (expanded) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [ref, expanded]);

    useEffect(() => {
        setWorkingDate(moment(currentDate).isValid() ? moment(currentDate) : moment(new Date()));
    }, [currentDate]);

    const withinBoundary = (boundaryVar, minVal, maxVal) => {
        return boundaryVar === undefined ? true : minVal <= maxVal;
    };

    const handleMonthClick = (selectedMonth) => {
        setWorkingDate(moment(selectedMonth));
        setDisplayState(DisplayStateEnum.DAY);
    };

    const handleYearClick = (selectedYear) => {
        setWorkingDate(moment(selectedYear));
        setDisplayState(DisplayStateEnum.MONTH);
    };

    const displaySection = (displayState) => {
        switch (displayState) {
            case DisplayStateEnum.DAY: {
                const dayArray = [];
                let tempDate = moment(workingDate).utc(true).startOf('month');
                let dayInWeek = Number(tempDate.format('d')); // Sun(0), Mon(1), ... Sat(6)
                dayInWeek = dayInWeek === 0 ? 6 : dayInWeek - 1; // Converted - Mon(0), Tue(1), ... Sun(6)

                tempDate.subtract(dayInWeek, 'days'); // Clock back to Monday of the week

                let key = 0;
                for (let i = 0; i < dayInWeek; i++) {
                    const availableDay =
                        withinBoundary(props.minDate, props.minDate, tempDate) &&
                        withinBoundary(props.maxDate, tempDate, props.maxDate);

                    dayArray.push(
                        <DatePickerCell
                            id={`${props.id}DPDay${key}`}
                            key={key}
                            isFocused={false}
                            isDisabled={!availableDay}
                            nonFocusedCellStyleOverride={{
                                fontSize: 18
                            }}
                            value={tempDate.date()}
                            date={moment(tempDate)}
                            onClick={onDateUpdate}
                        />
                    );
                    tempDate.add(1, 'days');
                    key++;
                }

                for (let day = 1; day <= workingDate.daysInMonth(); day++) {
                    const today =
                        workingDate.year() === selectedDate.year() &&
                        workingDate.month() === selectedDate.month() &&
                        day === selectedDate.date();

                    const availableDay =
                        withinBoundary(props.minDate, props.minDate, tempDate) &&
                        withinBoundary(props.maxDate, tempDate, props.maxDate);

                    dayArray.push(
                        <DatePickerCell
                            id={`${props.id}DPDay${key}`}
                            key={key}
                            styleOptions={{
                                cellBackgroundColour: '#FFFFFF'
                            }}
                            focusedCellStyleOverride={{ fontSize: 18 }}
                            value={day}
                            isSelected={today}
                            isDisabled={!availableDay}
                            date={moment(tempDate)}
                            onClick={onDateUpdate}
                        />
                    );
                    tempDate.add(1, 'days');
                    key++;
                }

                tempDate = moment(workingDate).endOf('month').startOf('day');
                dayInWeek = Number(tempDate.format('d')); // Sun(0), Mon(1), ... Sat(6)
                dayInWeek = dayInWeek === 0 ? 6 : dayInWeek - 1; // Converted - Mon(0), Tue(1), ... Sun(6)

                for (let i = dayInWeek; i < 6; i++) {
                    tempDate.add(1, 'days');

                    const availableDay =
                        withinBoundary(props.minDate, props.minDate, tempDate) &&
                        withinBoundary(props.maxDate, tempDate, props.maxDate);

                    dayArray.push(
                        <DatePickerCell
                            id={`${props.id}DPDay${key}`}
                            key={key}
                            isFocused={false}
                            isDisabled={!availableDay}
                            nonFocusedCellStyleOverride={{ fontSize: 18 }}
                            value={tempDate.date()}
                            date={moment(tempDate)}
                            onClick={onDateUpdate}
                        />
                    );
                    key++;
                }

                const dayStrings = new moment().localeData().weekdaysShort();
                const dayStringsArray = [];

                for (let index = 0; index < 7; index++) {
                    dayStringsArray.push(
                        <DatePickerCell
                            key={dayStrings[index]}
                            isFocused={false}
                            nonFocusedCellStyleOverride={{
                                backgroundColor: '#F0F0F0',
                                fontWeight: index >= 5 ? 'bold' : 500,
                                cursor: 'default'
                            }}
                            value={dayStrings[index]}
                        />
                    );
                }

                return (
                    <div
                        style={{
                            display: 'grid',
                            gridAutoRows: 32.66,
                            gridTemplateColumns: 'repeat(7, 1fr)',
                            columnGap: 2,
                            rowGap: 2,
                            margin: 2
                        }}
                    >
                        {dayStringsArray.map((day, i) => day)}
                        {dayArray.map((d, i) => d)}
                    </div>
                );
            }
            case DisplayStateEnum.MONTH: {
                const tempDate = moment(workingDate).startOf('year');

                const monthArray = [];

                // To do the boundary test for the month we need to set the date to the start of the month
                const tempMin = props.minDate
                    ? moment(props.minDate).startOf('month')
                    : props.minDate;
                const tempMax = props.maxDate
                    ? moment(props.maxDate).startOf('month')
                    : props.maxDate;

                for (let index = 0; index < 12; index++) {
                    const beforeThisMonth =
                        tempDate.year() < selectedDate.year() ||
                        (tempDate.year() <= selectedDate.year() &&
                            tempDate.month() < selectedDate.month());

                    const thisMonth =
                        tempDate.year() === selectedDate.year() &&
                        tempDate.month() === selectedDate.month();

                    const tempDateMonth = moment(tempDate).startOf('month');

                    const availableMonth =
                        withinBoundary(tempMin, tempMin, tempDateMonth) &&
                        withinBoundary(tempMax, tempDateMonth, tempMax);

                    const monthString = tempDate.format('MMM');
                    monthArray.push(
                        <DatePickerCell
                            id={`${monthString}DPMonth`}
                            key={`month${index}`}
                            value={tempDate.format('MMM')}
                            isSelected={thisMonth}
                            styleOptions={{
                                cellBackgroundColour: beforeThisMonth ? '#F0F0F0' : '#FFFFFF'
                            }}
                            isDisabled={!availableMonth}
                            date={moment(tempDate)}
                            onClick={handleMonthClick}
                        />
                    );

                    tempDate.add(1, 'months');
                }

                // This handles the months for the next year
                for (let index = 0; index < 4; index++) {
                    const tempDateMonth = moment(tempDate).startOf('month');

                    const availableMonth =
                        withinBoundary(tempMin, tempMin, tempDateMonth) &&
                        withinBoundary(tempMax, tempDateMonth, tempMax);

                    const monthString = tempDate.format('MMM');
                    monthArray.push(
                        <DatePickerCell
                            id={`${monthString}DPMonth2`}
                            key={`month2${index}`}
                            value={tempDate.format('MMM')}
                            styleOptions={{
                                cellTextColour: '#818181',
                                cellBorderColour: '#979797'
                            }}
                            isDisabled={!availableMonth}
                            date={moment(tempDate)}
                            onClick={handleMonthClick}
                        />
                    );

                    tempDate.add(1, 'months');
                }

                return (
                    <div
                        style={{
                            display: 'grid',
                            gridTemplateRows: 'repeat(4, 54.5px)',
                            gridTemplateColumns: 'repeat(4, 1fr)',
                            columnGap: 2,
                            rowGap: 2,
                            margin: 2
                        }}
                    >
                        {monthArray.map((m, i) => m)}
                    </div>
                );
            }
            case DisplayStateEnum.YEAR: {
                const tempDate = moment(workingDate);
                let year = workingDate.year() - 4;
                tempDate.year(year);
                const yearArray = [];

                // To do the boundary test for the year we need to set the date to the start of the year
                const tempMin = props.minDate
                    ? moment(props.minDate).startOf('year')
                    : props.minDate;
                const tempMax = props.maxDate
                    ? moment(props.maxDate).startOf('year')
                    : props.maxDate;

                for (let index = 0; index < 16; index++) {
                    year = tempDate.year();
                    const beforeThisYear = year < selectedDate.year();
                    const thisYear = year === selectedDate.year();

                    const tempDateYear = moment(tempDate).startOf('year');

                    const availableYear =
                        withinBoundary(tempMin, tempMin, tempDateYear) &&
                        withinBoundary(tempMax, tempDateYear, tempMax);

                    yearArray.push(
                        <DatePickerCell
                            id={`${year}DPYear`}
                            key={year}
                            value={year}
                            styleOptions={{
                                cellBackgroundColour: beforeThisYear ? '#F0F0F0' : '#FFFFFF'
                            }}
                            isSelected={thisYear}
                            isDisabled={!availableYear}
                            date={moment(tempDate)}
                            onClick={handleYearClick}
                        />
                    );
                    tempDate.add(1, 'years');
                }

                return (
                    <div
                        style={{
                            display: 'grid',
                            gridTemplateRows: 'repeat(4, 54.5px)',
                            gridTemplateColumns: 'repeat(4, 1fr)',
                            columnGap: 2,
                            rowGap: 2,
                            margin: 2
                        }}
                    >
                        {yearArray.map((y, i) => y)}
                    </div>
                );
            }
            default:
                return null;
        }
    };

    const handleReset = () => {
        setWorkingDate(selectedDate);
        setDisplayState(DisplayStateEnum.DAY);
    };

    return !expanded
        ? null
        : createPortal(
            <div
                id={`${props.id}Container`}
                style={{ ...bodyContainerStyle,
                    minWidth: boundingRect.width,
                    top:
                          boundingRect.top +
                          boundingRect.height +
                          window.scrollY,
                    left: boundingRect.left + window.scrollX - widthOffset,
                    position: 'absolute',
                    ...props.bodyContainerStyle
                }}
                ref={ref}
            >
                <DatePickerHeader
                    id={props.id}
                    workingDate={workingDate}
                    minDate={props.minDate}
                    maxDate={props.maxDate}
                    displayState={displayState}
                    onUpdateWorkingDate={setWorkingDate}
                    onTitleClick={setDisplayState}
                    onReset={handleReset}
                />
                <div
                    id={`${props.id}Body`}
                    style={{ ...bodyBackgroundStyle, ...props.bodyStyle }}
                >
                    {displaySection(displayState)}
                </div>
            </div>, document.body
        );
};

export default DatePickerBody;
